export interface SubdomainWithWeights {
  id: number;
  name: string;
  domain_id: number;
  discovery_weight: number;
  foundational_weight: number;
  emerging_weight: number;
  developing_weight: number;
  advancing_weight: number;
  thriving_weight: number;
}

export interface UserResponse {
  subdomain_id: number;
  selected_level: number;
  subdomains: SubdomainWithWeights & {
    domains: {
      id: number;
      name: string;
    };
  };
}

export interface DomainScore {
  id: number;
  name: string;
  totalScore: number;
  maxPossibleScore: number;
  percentage: number;
  subdomainScores: {
    id: number;
    name: string;
    score: number;
    maxScore: number;
    selectedLevel: number;
  }[];
}

export const calculateDomainScores = (responses: UserResponse[]): DomainScore[] => {
  const domainScores: { [key: number]: DomainScore } = {};

  responses.forEach((response) => {
    const subdomain = response.subdomains;
    const domain = subdomain.domains;
    const selectedLevel = response.selected_level;

    if (!domainScores[domain.id]) {
      domainScores[domain.id] = {
        id: domain.id,
        name: domain.name,
        totalScore: 0,
        maxPossibleScore: 0,
        percentage: 0,
        subdomainScores: []
      };
    }
    const actualScore = calculateSubdomainScore(subdomain, selectedLevel);
    const maxScore = getMaxSubdomainScore(subdomain);

    domainScores[domain.id].totalScore += actualScore;
    domainScores[domain.id].maxPossibleScore += maxScore;
    domainScores[domain.id].subdomainScores.push({
      id: subdomain.id,
      name: subdomain.name,
      score: actualScore,
      maxScore: maxScore,
      selectedLevel: selectedLevel
    });
  });

  // Oblicz procenty
  Object.values(domainScores).forEach((domain) => {
    domain.percentage = domain.maxPossibleScore > 0 
      ? Math.round((domain.totalScore / domain.maxPossibleScore) * 100)
      : 0;
  });

  return Object.values(domainScores);
};

export const calculateSubdomainScore = (subdomain: SubdomainWithWeights, selectedLevel: number): number => {
  switch (selectedLevel) {
    case 0: return 0; 
    case 1: return subdomain.discovery_weight || 1;
    case 2: return subdomain.foundational_weight || 2;
    case 3: return subdomain.emerging_weight || 3;
    case 4: return subdomain.developing_weight || 4;
    case 5: return subdomain.advancing_weight || 5;
    case 6: return subdomain.thriving_weight || 6;
    default: return 0;
  }
};

export const getMaxSubdomainScore = (subdomain: SubdomainWithWeights): number => {
  return subdomain.thriving_weight || 6;
};

export const getLevelName = (level: number): string => {
  const levels = {
    0: 'Not Applicable',
    1: 'Discovery',
    2: 'Foundational',
    3: 'Emerging',
    4: 'Developing',
    5: 'Advancing',
    6: 'Thriving'
  };
  return levels[level as keyof typeof levels] || 'Unknown';
};

export const getLevelColor = (level: number): string => {
  switch (level) {
    case 1:
      return '#9F2926'; 
    case 2:
      return '#E59836'; 
    case 3:
      return '#583B64'; 
    case 4:
      return '#76BAA2'; 
    case 5:
      return '#3D89A6'; 
    case 6:
      return '#727272'; 
    default:
      return '#727272'; 
  }
};

export const getScoreColor = (percentage: number): string => {
  if (percentage >= 80) return '#10b981'; // green-500
  if (percentage >= 60) return '#f59e0b'; // amber-500
  if (percentage >= 40) return '#f97316'; // orange-500
  return '#ef4444'; // red-500
};

export const getScoreColorClass = (percentage: number): string => {
  if (percentage >= 80) return 'text-green-600 bg-green-50';
  if (percentage >= 60) return 'text-amber-600 bg-amber-50';
  if (percentage >= 40) return 'text-orange-600 bg-orange-50';
  return 'text-red-600 bg-red-50';
};

export const calculateOverallScore = (domainScores: DomainScore[]): number => {
  if (domainScores.length === 0) return 0;
  
  const totalScore = domainScores.reduce((sum, domain) => sum + domain.totalScore, 0);
  const totalMaxScore = domainScores.reduce((sum, domain) => sum + domain.maxPossibleScore, 0);
  
  return totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0;
}; 